// Full-screen editor status views: loading, login redirect, and project-access errors.
import { useEffect, useState } from 'react'
import { LoadingMascot, VelticaMascot } from '@webbuilder/ui'
import { DASHBOARD_URL } from './appConfig'
import { clearIdentityRetries, dashboardLoginUrl, takeIdentityRetry, type ProjectLoadError } from './appUtils'
import { useAuthStore } from './store/auth.store'
import { useI18n } from './lib/i18n'

/** The stages the editor moves through before the canvas is ready. */
export type BootPhase = 'session' | 'redirecting' | 'project'

/** Tiempo durante el que la pantalla deja ver gradualmente el editor debajo. */
export const EDITOR_BOOT_EXIT_MS = 300

export type EditorBootState = {
  visible: boolean
  spinning: boolean
}

/**
 * La pantalla completa necesita más despedida que la mascota suelta.
 *
 * Vive en el editor para no ralentizar los loaders pequeños del dashboard. El
 * estado se ajusta durante el render por la misma razón que el hook compartido:
 * evita un fotograma intermedio sin capa antes de arrancar la transición.
 */
export function useEditorBoot(loading: boolean): EditorBootState {
  const [farewell, setFarewell] = useState(false)
  const [previousLoading, setPreviousLoading] = useState(loading)

  if (previousLoading !== loading) {
    setPreviousLoading(loading)
    setFarewell(!loading)
  }

  useEffect(() => {
    if (loading || !farewell) return
    const timer = window.setTimeout(() => setFarewell(false), EDITOR_BOOT_EXIT_MS)
    return () => window.clearTimeout(timer)
  }, [loading, farewell])

  return { visible: loading || farewell, spinning: loading }
}

/* En claves: constante de módulo. */
const BOOT_LABEL_KEYS: Record<BootPhase, string> = {
  session: 'boot.checkingSession',
  redirecting: 'boot.redirecting',
  project: 'boot.loadingProject',
}

/**
 * La pantalla de arranque, una sola para todas las etapas.
 *
 * La mascota girando y, debajo, en qué va. Es la MISMA espera que el panel usa
 * en sus listas y sus fichas: cruzar del panel al editor y encontrarse otro
 * indicador se lee como cambiar de producto, y este cruce ocurre cada vez que
 * alguien abre un proyecto.
 *
 * Antes eran el logotipo respirando sobre una barra indeterminada. La barra
 * sobra: no medía nada —era un rectángulo yendo y viniendo— y prometía un
 * progreso que nadie estaba calculando.
 *
 * El mismo componente en todas las etapas mantiene la mascota montada, así que
 * al pasar de sesión a proyecto sólo cambia el rótulo y el giro no se reinicia.
 */
export function EditorBoot({ phase, spinning = true }: {
  phase: BootPhase
  /** Falso arranca la despedida: la mascota se desvanece al dar paso. */
  spinning?: boolean
}) {
  const { t } = useI18n()
  return (
    <div className={`editor-boot${spinning ? '' : ' is-leaving'}`} aria-busy={spinning}>
      <div className="editor-boot__inner">
        <LoadingMascot spinning={spinning} size={56} />
        <div className="editor-boot__status" role="status" aria-live="polite">
          <span key={phase}>{t(BOOT_LABEL_KEYS[phase])}</span>
        </div>
      </div>
    </div>
  )
}

// Bounded so a handoff that keeps failing (expired token, misconfigured worker)
// surfaces an error instead of ping-ponging with the dashboard forever.
const MAX_HANDOFF_ATTEMPTS = 3

function EditorErrorView({
  title,
  message,
  onReturn,
}: {
  title: string
  message: string
  onReturn: () => void
}) {
  const { t } = useI18n()
  return (
    <main className="editor-error-view">
      <section className="editor-error-card" role="alert" aria-live="assertive">
        <div className="editor-error-brand">
          <span className="editor-error-mascot"><VelticaMascot size={42} /></span>
          <img className="editor-error-logo" src="/veltica.svg" alt="Véltica" decoding="async" />
        </div>
        <div className="editor-error-copy">
          <h1>{title}</h1>
          <p>{message}</p>
        </div>
        <button className="editor-error-primary" type="button" onClick={onReturn}>
          {t('boot.backToDashboard')}
        </button>
      </section>
    </main>
  )
}

function nextHandoffAttempt(): number {
  try {
    const attempts = Number(sessionStorage.getItem('veltica_handoff_attempts') ?? '0') + 1
    sessionStorage.setItem('veltica_handoff_attempts', String(attempts))
    return attempts
  } catch {
    return 1
  }
}

export function RedirectToDashboardLogin() {
  const { t } = useI18n()
  const looping = nextHandoffAttempt() > MAX_HANDOFF_ATTEMPTS

  useEffect(() => {
    if (looping) return
    window.location.replace(dashboardLoginUrl())
  }, [looping])

  if (looping) {
    return <EditorErrorView
      title={t('boot.signInFailed')}
      message={t('boot.openProjectAgain')}
      onReturn={() => {
        try { sessionStorage.removeItem('veltica_handoff_attempts') } catch { /* private mode */ }
        window.location.href = DASHBOARD_URL
      }}
    />
  }

  return <EditorBoot phase="redirecting" />
}

/**
 * El editor tiene sesión, pero de otra cuenta que la que abrió el proyecto.
 *
 * Pasa porque cada origen guarda su propia sesión y el editor sólo pide un
 * handoff cuando no tiene ninguna: al cambiar de cuenta en el dashboard, aquí
 * seguía la anterior. Se descarta y se vuelve al dashboard, que emite el handoff
 * de la cuenta correcta.
 */
// Un intento por carga de página, contado fuera de React: el cuerpo y los
// efectos de un componente corren dos veces en desarrollo (StrictMode) y se
// comerían el presupuesto de reintentos de una sentada.
let identityReauthStarted = false

export function SwitchAccountForProject() {
  const { t } = useI18n()
  const [exhausted, setExhausted] = useState(false)

  useEffect(() => {
    if (identityReauthStarted) return
    identityReauthStarted = true
    if (!takeIdentityRetry()) { setExhausted(true); return }
    void useAuthStore.getState().signOutAndReauth()
  }, [])

  if (exhausted) {
    return <EditorErrorView
      title={t('boot.otherAccount')}
      message={t('boot.otherAccountHelp')}
      onReturn={() => {
        clearIdentityRetries()
        window.location.href = DASHBOARD_URL
      }}
    />
  }

  return <EditorBoot phase="redirecting" />
}

export function ProjectAccessError({ error }: { error: ProjectLoadError }) {
  const { t } = useI18n()
  /* El texto del servidor gana cuando lo hay: es más concreto que cualquier
     frase nuestra, aunque venga en otro idioma. */
  const message = error.message ?? (error.messageKey ? t(error.messageKey, error.vars) : '')
  return <EditorErrorView
    title={t(error.titleKey)}
    message={message}
    onReturn={() => { window.location.href = DASHBOARD_URL }}
  />
}
